import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IModule } from 'src/app/interfaces/IModule';
import { combineLatest, debounceTime, Subject } from 'rxjs';
import { DepartmentalQuestionnairesService } from 'src/app/services/departmentalQuestionnaires.service';
import { paths } from 'src/component-paths/component-paths';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { Router } from '@angular/router';
import { PatientService } from 'src/app/services/patient.service';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { getQuestionnaireTemplateConfig } from 'src/app/constants/questionnaire-template-registry';
import { QuestionnaireNavigationService } from 'src/app/services/questionnaireNavigation.service';

@Component({
  selector: 'app-speech-therapy-questionnaire',
  templateUrl: './speech-therapy-questionnaire.component.html',
  styleUrl: './speech-therapy-questionnaire.component.css',
  standalone: false,
})
export class SpeechTherapyQuestionnaireComponent implements OnInit {
  @Input() modules?: IModule[];
  componentPaths = paths;
  @Input() selectedOption?: string;
  @Output() selectedOptionChange = new EventEmitter<string>();
  @Input() readonlyMode = false;
  @Input() overviewItemsByTemplateId: Record<
    number,
    QuestionnaireHistoryItem[]
  > = {};
  @Input() overviewLoading = false;
  @Input() overviewError = '';
  @Output() overviewRefreshRequested = new EventEmitter<void>();

  constructor(
    private readonly departmentalQuestionnairesService: DepartmentalQuestionnairesService,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly router: Router,
    private readonly questionnaireNavigationService: QuestionnaireNavigationService,
  ) {}

  ngOnInit() {}

  private scrollSubject = new Subject<Event>();

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest([
        this.departmentalQuestionnairesService
          .departmentalQuestionnairesLoadingSub$,
      ]).subscribe(([value1]) => {
        const sectionPath = this.modules?.find(
          (sm) => sm.path == this.selectedOption,
        )?.path;
        if (!value1 && sectionPath) {
          const element = document.getElementById(sectionPath);
          if (element) {
            // menu, patient header, title, margin
            const offset = element.offsetTop - 120 - 66 - 64 - 16;
            window.scroll({
              top: offset,
              behavior: 'smooth',
            });

            this.scrollSubject
              .pipe(
                debounceTime(100), // Adjust debounce time as needed (in milliseconds)
              )
              .subscribe(() => {
                // Your existing scroll logic here
                const gds = document.getElementById(
                  paths.geriatricDepressionScale,
                );
                const pd = document.getElementById(
                  paths.parkinsonsQuestionnaire,
                );
                const bdi = document.getElementById(
                  paths.becksDepressionInventory,
                );
                const ocad = document.getElementById(
                  paths.oralClinicalAssessmentOfDeposition,
                );
                if (gds) {
                  const rect = gds.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.modules?.find(
                      (sm) => sm.path == paths.geriatricDepressionScale,
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
                if (pd) {
                  const rect = pd.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.modules?.find(
                      (sm) => sm.path == paths.parkinsonsQuestionnaire,
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
                if (bdi) {
                  const rect = bdi.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.modules?.find(
                      (sm) => sm.path == paths.becksDepressionInventory,
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
                if (ocad) {
                  const rect = ocad.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.modules?.find(
                      (sm) =>
                        sm.path == paths.oralClinicalAssessmentOfDeposition,
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
              });
          }
        }
      });
    }, 500);
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  isVisitMode(): boolean {
    return (
      !!this.eocService.currentVisitId() &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  currentVisitId(): string | number | undefined {
    return this.eocService.currentVisitId() || undefined;
  }

  currentPatientId(): string | undefined {
    return this.patientService.patientIDsignal() || undefined;
  }

  currentEpisodeOfCareId(): string | undefined {
    return this.eocService.currentEocId() || undefined;
  }

  getTemplateItems(templateId: number): QuestionnaireHistoryItem[] {
    return this.overviewItemsByTemplateId[templateId] ?? [];
  }

  openVisitQuestionnaire(templateId: number): void {
    const routeSection =
      getQuestionnaireTemplateConfig(templateId)?.routeSection;

    if (!this.isVisitMode() || !routeSection) {
      return;
    }

    this.questionnaireNavigationService.setTemplateContext(templateId);
    this.questionnaireNavigationService.openScreen({
      source: this.router.url.includes('inPatientView')
        ? 'inPatientView'
        : 'episodeOfCare',
      mode: 'visit-add',
      routeSection,
      questionnaireTemplateId: templateId,
    });
  }

  handleVisitQuestionnaireSubmitted(): void {
    this.overviewRefreshRequested.emit();
  }

  openQuestionnaireSubmission(item: QuestionnaireHistoryItem): void {
    const templateId = Number(item.questionnaireTemplateId ?? 0);
    const questionnaireId = item.questionnaireId?.toString();
    const routeSection =
      getQuestionnaireTemplateConfig(templateId)?.routeSection;

    if (!templateId || !questionnaireId || !routeSection) {
      return;
    }

    this.questionnaireNavigationService.setTemplateContext(templateId);

    const source = this.isVisitMode()
      ? this.router.url.includes('inPatientView')
        ? 'inPatientView'
        : 'episodeOfCare'
      : 'profile';

    this.questionnaireNavigationService.openScreen({
      source,
      mode:
        this.isVisitMode() && item.canEdit ? 'visit-edit' : 'profile-readonly',
      routeSection,
      questionnaireTemplateId: templateId,
      questionnaireDataId: questionnaireId,
    });
  }
}
